import React, { PropsWithChildren, Suspense, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { LoadingIndicator } from '@spotify-internal/encore-web';
import { ManagedRemoteConfigProvider } from '@spotify-internal/remote-config-integration-react';
import { Configuration } from '@spotify-internal/remote-config-properties';
import { BrowserResolver } from '@spotify-internal/remote-config-resolver-browser';
import { useUser } from '@hooks/useUser';
import { useTransport } from '@lib/transport/transport';
import { isNotProduction } from '@helpers/env';
import { properties } from './properties';

// @ts-ignore
const DynamicComponent = dynamic(() =>
  import('../_testHelpers_/remoteConfigHelper').then(
    mod => mod.TestRemoteConfigProvider,
  ),
);

export const RemoteConfigProvider = ({ children }: PropsWithChildren<{}>) => {
  const { userInfo } = useUser();
  let resolveContext: null | { username: string | null } = null;
  const transport = useTransport();

  if (userInfo?.profile) {
    resolveContext = { username: userInfo.profile.username };
  }

  useEffect(() => {
    if (transport) {
      transport
        .connect()
        .then(() => {
          transport.authenticate().catch((error: Error) => {
            if (isNotProduction) {
              console.log('Error authenticating transport: ', error.message); //eslint-disable-line
            }
            return;
          });
        })
        .catch((error: unknown) => {
          // Ignore transport error when logged out (e.g. oauth iframe timeout)
          if (isNotProduction) {
            // eslint-disable-next-line no-console
            console.log(
              'Error connecting transport:',
              error instanceof Error
                ? error.message
                : (error as { error?: string })?.error,
            );
          }
        });
    }
  }, [transport]);

  // Don't render ManagedRemoteConfigProvider if transport instance isn't ready
  if (!transport) {
    return <>{children}</>;
  }

  if (window?.Cypress && window?.useStubSilentAuth) {
    return (
      <DynamicComponent>
        <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
      </DynamicComponent>
    );
  }

  return (
    <ManagedRemoteConfigProvider
      resolver={BrowserResolver}
      options={{
        clientId: 'atlas-web',
        timeout: 5000,
        transport,
        properties,
        backgroundFetchInterval: 2147483647,
      }}
      initialConfiguration={Configuration.empty()}
      context={resolveContext}
    >
      <Suspense fallback={<LoadingIndicator />}>{children}</Suspense>
    </ManagedRemoteConfigProvider>
  );
};
